import { useCallback, useId, useState } from 'react'
import { Upload, X, FileSpreadsheet, FileText, Image as ImageIcon } from 'lucide-react'
import {
  IDEA_ATTACHMENT_EXTENSIONS,
  IDEA_ATTACHMENT_MAX_FILES,
  validateIdeaAttachmentFile,
} from '@/idea-board/lib/attachmentValidation'

function fileIcon(name) {
  const lower = (name || '').toLowerCase()
  if (/\.(jpg|jpeg|png)$/.test(lower)) return ImageIcon
  if (/\.(xlsx|xls)$/.test(lower)) return FileSpreadsheet
  return FileText
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function IdeaAttachmentDropzone({ files, onChange, disabled }) {
  const inputId = useId()
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState(null)

  const addFiles = useCallback(
    (incoming) => {
      setError(null)
      const list = Array.from(incoming || [])
      if (!list.length) return

      const next = [...files]
      for (const file of list) {
        if (next.length >= IDEA_ATTACHMENT_MAX_FILES) {
          setError(`Maximum ${IDEA_ATTACHMENT_MAX_FILES} files per idea`)
          break
        }
        const err = validateIdeaAttachmentFile(file)
        if (err) {
          setError(err)
          continue
        }
        if (next.some((f) => f.name === file.name && f.size === file.size)) continue
        next.push(file)
      }
      onChange(next)
    },
    [files, onChange],
  )

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      setDragOver(false)
      if (disabled) return
      addFiles(e.dataTransfer?.files)
    },
    [addFiles, disabled],
  )

  const removeFile = (index) => {
    setError(null)
    onChange(files.filter((_, i) => i !== index))
  }

  const accept = IDEA_ATTACHMENT_EXTENSIONS.join(',')

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-border bg-secondary/30'
        } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <input
          id={inputId}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          disabled={disabled}
          onChange={(e) => {
            addFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm font-medium mb-0.5">Drag & drop attachments here</p>
        <p className="text-xs text-muted-foreground mb-2">
          Screenshots, Excel, PDF, or Word — up to {IDEA_ATTACHMENT_MAX_FILES} files, 10 MB each
        </p>
        <label htmlFor={inputId}>
          <span
            className={`inline-block px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground ${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-primary/90'
            }`}
          >
            Choose files
          </span>
        </label>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((file, index) => {
            const Icon = fileIcon(file.name)
            return (
              <li
                key={`${file.name}-${file.size}-${index}`}
                className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm"
              >
                <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="flex-1 min-w-0 truncate">{file.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">{formatBytes(file.size)}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
